import type { Billing, Plan, AuthUser, AdjustData } from '@/models/interfaces'
import { auth } from '@/services/auth'
import { backend } from '@/services/backend'
import { platform } from '@/services/platform'
import { Sentry } from '@/services/sentry'
import { request } from '@/services/request'
import { storage } from '@/helpers/storage'

import { waitWithTimeout } from '@/helpers/timer'
import { AdjustWrapper } from '@/analytics/adjust'
import {
  Mixpanel,
  login as mixpanelLogin,
  register as mixpanelRegister,
} from '@/analytics/mixpanel'
import { StorageKeys } from '@/helpers/storage.keys'
import { GTM } from '@/analytics/gtm'
import { Bing } from '@/analytics/bing'
import type { AuthType } from '@/models/interfaces.auth'

export async function trackLogin(user: AuthUser): Promise<void> {
  await _tryOperation(async () => {
    await waitWithTimeout(
      (done) => {
        mixpanelLogin(user, done)
        GTM.setUserID(user.id)
      },
      1,
      1000,
    )
  })
}

export async function saveMarketingUrl(): Promise<void> {
  await _tryOperation(async () => {
    const marketingUrl = storage.getItem(StorageKeys.marketingUrl)
    if (marketingUrl) {
      // Save marketing URL if we have it.
      await request.post('billing/marketing_url', {
        marketing_url: marketingUrl,
      })
    }
  })
}

export async function trackSignup(
  user: AuthUser,
  signupType: AuthType,
): Promise<void> {
  await _tryOperation(async () => {
    GTM.register(user)
    Bing.register()
    await AdjustWrapper.register()

    // Mixpanel call is important, so wait for it separately.
    await waitWithTimeout(
      (done) => {
        mixpanelRegister(user, signupType, done)
      },
      1,
      1000,
    )
  })
}

export async function trackSubscribe(
  new_plan: Plan,
  billing: Billing,
): Promise<void> {
  // Here we need to track new plan vs previous billing data, so
  // we can have from -> to information, so passing in the "old"
  // billing data.
  // But this billing data is not "old" for Apple billing. Instead it is the
  // data updated after the user has subscribed, so be careful how you use it.
  await _tryOperation(async () => {
    await waitWithTimeout(
      (done) => {
        Mixpanel.trackBilling(new_plan, billing, done)
        GTM.subscribe(auth.getUser(), new_plan, billing)
        // Note: trackers below don't have callback,
        // so we just invoke then and hope it'll finish before we navigate
        // to another page.
        Bing.subscribe(auth.getUser(), new_plan, billing)
      },
      1,
      1000,
    )
  })
}

/**
 * If the user is logged in and these analytics IDs are not set, set them:
 * - Google Analytics 4 client ID
 * - IDFA, iOS Advertising Identifier (iOS native app only)
 * - GPS ADID, Google Play Services Advertiser ID (Android native app only)
 * - adid, Adjust identifier
 * TODO: Maybe there could be a cleaner way of doing this.
 */
export async function maybeSyncAnalyticsIds(): Promise<void> {
  if (!auth.loggedIn()) {
    return
  }
  const isIOS = await platform.isNativeIOSApp()
  const isAndroid = await platform.isNativeAndroidApp()
  await _tryOperation(async () => {
    await waitWithTimeout(
      (done) => {
        if (!auth.getUser()?.ga4_client_id) {
          GTM.getGa4ClientId(async (clientId: string) => {
            await backend.setGa4ClientId(clientId)
            auth.setGa4ClientId(clientId)
            done()
          })
        } else {
          done()
        }
      },
      1,
      1000,
    )
    const newAdjustData: AdjustData = {}
    if (isIOS) {
      if (!auth.getUser()?.idfa) {
        const idfa = await AdjustWrapper.maybeGetIdfa()
        if (idfa !== '') {
          newAdjustData.idfa = idfa
        }
      }
      if (!auth.getUser()?.adid_ios) {
        const adid_ios = await AdjustWrapper.getAdid()
        if (adid_ios !== '') {
          newAdjustData.adid_ios = adid_ios
        }
      }
    } else if (isAndroid) {
      if (!auth.getUser()?.gps_adid) {
        const gps_adid = await AdjustWrapper.maybeGetGpsAdid()
        if (gps_adid !== '') {
          newAdjustData.gps_adid = gps_adid
        }
      }
      if (!auth.getUser()?.adid_android) {
        const adid_android = await AdjustWrapper.getAdid()
        if (adid_android !== '') {
          newAdjustData.adid_android = adid_android
        }
      }
    } else {
      if (!auth.getUser()?.adid_web) {
        const adid_web = await AdjustWrapper.getAdid()
        if (adid_web !== '') {
          newAdjustData.adid_web = adid_web
        }
      }
    }
    if (Object.values(newAdjustData).some((val) => val)) {
      await backend.setAdjustData(newAdjustData)
      auth.setAdjustData(newAdjustData)
    }
  })
}

type Operation = () => Promise<void>

async function _tryOperation(operation: Operation): Promise<void> {
  try {
    await operation()
  } catch (error) {
    Sentry.captureException(error)
  }
}
